import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import api from '../../services/api';
import { useDateFormat } from '../../hooks/useDateFormat';
import {
  timesheetEntryDateKey,
  timesheetEntryHoursLabel,
  timesheetEntryUserLabel
} from '../../utils/timesheetEntryDisplay';
import { formatTimesheetSectionTitle } from '../../utils/accountDashboardFocus';
import { useTaskDashboardOptional } from './TaskDashboardContext';

interface TimesheetEntry {
  id: string;
  taskId: string;
  startTime?: string;
  stopTime?: string;
  hours?: number;
  description?: string;
  user?: {
    displayName?: string;
    email?: string;
  };
}

interface TaskTimesheetSectionProps {
  taskId: string;
  organizationId: string;
}

export default function TaskTimesheetSection({ taskId, organizationId }: TaskTimesheetSectionProps) {
  const dashboard = useTaskDashboardOptional();
  const { formatDateOnly, formatTimeOnly } = useDateFormat();
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const focusWindow = dashboard?.focusWindow;
  const accountTimezone = dashboard?.accountTimezone ?? 'UTC';
  const granularity = dashboard?.granularity ?? 'month';
  const sectionTitle = formatTimesheetSectionTitle(focusWindow?.label, granularity);

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);

      let startDate: string;
      let endDate: string;
      if (focusWindow) {
        startDate = focusWindow.from;
        endDate = focusWindow.to;
      } else {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        startDate = start.toISOString().split('T')[0];
        endDate = end.toISOString().split('T')[0];
      }

      const response = await api.getTimesheetEntries({
        taskId,
        organizationId,
        startDate,
        endDate,
        timezone: accountTimezone
      });

      setEntries(response.data || []);
    } catch (err) {
      console.error('Error loading timesheet entries:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [taskId, organizationId, focusWindow, accountTimezone]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {sectionTitle}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <LinearProgress />
        ) : entries.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.slice(0, 20).map((entry) => {
                  const dateKey = timesheetEntryDateKey(entry);
                  return (
                    <TableRow key={entry.id}>
                      <TableCell>{dateKey ? formatDateOnly(dateKey) : 'N/A'}</TableCell>
                      <TableCell>
                        {entry.startTime && entry.stopTime ? (
                          <>
                            {formatTimeOnly(entry.startTime)} - {formatTimeOnly(entry.stopTime)}
                          </>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{timesheetEntryHoursLabel(entry)}</TableCell>
                      <TableCell>{timesheetEntryUserLabel(entry)}</TableCell>
                      <TableCell>
                        {entry.description ? (
                          <Typography
                            variant="body2"
                            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {entry.description}
                          </Typography>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No timesheet entries found for this period.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
